import {clsx} from 'clsx'
import {useCallback, useEffect, useRef} from 'react'

import {Omnibar, type OmnibarRef} from '../omnibar/omnibar'
import hierarchyDepthStyles from '../table/renderers/title-renderer.module.css'
import {CELL_HEIGHT} from './constants'
import styles from './sub-issue-omnibar-row.module.css'

type SubIssueOmnibarRowProps = {
  previousItemId: number
  onClose: () => void
  subIssueDepth?: number
}

/**
 * Wrapper component for rendering an Omnibar inline below a table row.
 * Handles closing the omnibar on blur or escape key.
 */
export function SubIssueOmnibarRow({previousItemId, onClose, subIssueDepth = 0}: SubIssueOmnibarRowProps) {
  const omnibarRef = useRef<OmnibarRef>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const omnibarDepth = subIssueDepth + 3

  useEffect(() => {
    // Focus the omnibar when it opens
    requestAnimationFrame(() => omnibarRef.current?.focus())
  }, [])

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (event.key === 'Escape') {
      onClose()
    }
  }

  // Handle focus leaving the omnibar container
  const handleFocusOut = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      // Check if the new focus target is outside the container
      if (containerRef.current && !containerRef.current.contains(event.relatedTarget as Node)) {
        onClose()
      }
    },
    [onClose],
  )

  return (
    <div
      ref={containerRef}
      onBlur={handleFocusOut}
      style={{
        height: `${CELL_HEIGHT}px`,
        '--hierarchy-depth': omnibarDepth,
      }}
      className={clsx(hierarchyDepthStyles.hierarchyDepth, styles.InlineOmnibar)}
    >
      <Omnibar
        ref={omnibarRef}
        defaultPlaceholder="Add a sub-issue"
        newItemAttributes={{
          previousItemId,
          isCreatingSubIssue: true,
        }}
        onKeyDown={handleKeyDown}
      />
    </div>
  )
}

try{ SubIssueOmnibarRow.displayName ||= 'SubIssueOmnibarRow' } catch {}